import type { ReactNode } from "react";

import {
  Badge,
  Card,
  DataList,
  Flex,
  Grid,
  Heading,
  Separator,
  Text,
} from "@radix-ui/themes";

import type {
  OrderDetailField,
  OrderDetailViewModel,
} from "./OrderDetailDialog.utils";
import { ORDERS_UNAVAILABLE_VALUE } from "./OrdersWidget.utils";

export const DetailFields = ({ fields }: { fields: OrderDetailField[] }) => (
  <DataList.Root size="2">
    {fields.map((field) => {
      const value =
        field.badgeColor && field.value !== ORDERS_UNAVAILABLE_VALUE ? (
          <Badge color={field.badgeColor} variant="soft">
            {field.value}
          </Badge>
        ) : field.numeric || field.monospace ? (
          <Text size="2" className="atd-numeric">
            {field.value}
          </Text>
        ) : (
          <Text size="2">{field.value}</Text>
        );

      return (
        <DataList.Item key={field.label} align="center">
          <DataList.Label color="gray" minWidth="140px">
            {field.label}
          </DataList.Label>
          <DataList.Value>{value}</DataList.Value>
        </DataList.Item>
      );
    })}
  </DataList.Root>
);

export const Section = ({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) => (
  <Flex direction="column" gap="3">
    <Heading as="h3" size="3">
      {title}
    </Heading>
    {children}
  </Flex>
);

export interface OrderDetailDialogSummaryPresentationProps {
  detail: OrderDetailViewModel;
  replacementHistory: ReactNode;
  fillsTable: ReactNode;
}

export const OrderDetailDialogSummaryPresentation = ({
  detail,
  replacementHistory,
  fillsTable,
}: OrderDetailDialogSummaryPresentationProps) => (
  <Flex direction="column" gap="4">
    <Section title="Focused order">
      <DetailFields fields={detail.summaryFields} />
    </Section>

    {detail.priceFields.length > 0 ? (
      <>
        <Separator size="4" />
        <Section title="Prices">
          <DetailFields fields={detail.priceFields} />
        </Section>
      </>
    ) : null}

    {detail.replacementFields.length > 0 ? (
      <>
        <Separator size="4" />
        <Section title="Replacement pointers">
          <DetailFields fields={detail.replacementFields} />
        </Section>
      </>
    ) : null}

    <Separator size="4" />
    {replacementHistory}

    {detail.legs.length > 0 ? (
      <>
        <Separator size="4" />
        <Section title="Order legs">
          <Grid columns={{ initial: "1", sm: "2" }} gap="2">
            {detail.legs.map((leg) => (
              <Card key={leg.id} variant="surface">
                <Flex direction="column" gap="3">
                  <Heading as="h4" size="2">
                    {leg.title}
                  </Heading>
                  <DetailFields fields={leg.fields} />
                </Flex>
              </Card>
            ))}
          </Grid>
        </Section>
      </>
    ) : null}

    <Separator size="4" />
    <Section title="Lifecycle milestones">
      <Text size="2" color="gray">
        Intermediate status transitions are unavailable. These are milestones
        present on the focused order only.
      </Text>
      {detail.milestones.length > 0 ? (
        <Flex direction="column" gap="2">
          {detail.milestones.map((milestone) => (
            <Flex
              key={`${milestone.label}-${milestone.timestamp}`}
              align="baseline"
              justify="between"
              gap="3"
              wrap="wrap"
            >
              <Text size="2" weight="medium">
                {milestone.label}
              </Text>
              <Text size="2" color="gray" className="atd-numeric">
                {milestone.displayTimestamp}
              </Text>
            </Flex>
          ))}
        </Flex>
      ) : (
        <Text size="2" color="gray">
          No lifecycle timestamps are available.
        </Text>
      )}
    </Section>

    <Separator size="4" />
    {fillsTable}
  </Flex>
);
